export const USER_STATUSES = ['active', 'locked', 'suspended', 'disabled', 'deleted'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const ACCOUNT_ACTIONS = ['suspend', 'restore', 'lock', 'unlock'] as const;
export type AccountAction = (typeof ACCOUNT_ACTIONS)[number];

export interface Money {
  amountPaise: number;
  currency: string;
}

export interface AdminCustomerSummary {
  id: string;
  fullName: string | null;
  email: string;
  phone: string | null;
  status: string;
  emailVerified: boolean;
  orderCount: number;
  totalSpend: Money;
  lastOrderAt: string | null;
  joinedAt: string;
  lastSeenAt: string | null;
}

export interface AdminCustomerAddress {
  label: string | null;
  recipientName: string;
  phone: string;
  city: string;
  state: string;
  pincode: string;
  isDefault: boolean;
}

export interface AdminCustomerOrderSummary {
  orderNumber: string;
  customerName: string | null;
  customerEmail: string;
  itemCount: number;
  subtotal: Money;
  discount: Money;
  deliveryFee: Money;
  tax: Money;
  total: Money;
  status: string;
  paymentStatus: string | null;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCustomerCoupon {
  code: string;
  discountApplied: Money;
  orderNumber: string;
  redeemedAt: string;
}

export interface AdminCustomerSpinHistoryEntry {
  outcome: unknown;
  spunAt: string;
}

export interface AdminCustomerRewardCoupon {
  code: string;
  isActive: boolean;
  issuedAt: string;
  expiresAt: string;
}

export interface AdminCustomerNotification {
  type: string;
  title: string;
  createdAt: string;
  readAt: string | null;
}

export interface AdminCustomerLoginActivity {
  eventType: string;
  ipAddress: string | null;
  createdAt: string;
}

export interface AdminCustomerProfile {
  id: string;
  fullName: string | null;
  email: string;
  phone: string | null;
  status: string;
  emailVerified: boolean;
  joinedAt: string;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  orderStats: { orderCount: number; totalSpend: Money; lastOrderAt: string | null };
  recentOrders: AdminCustomerOrderSummary[];
  addresses: AdminCustomerAddress[];
  coupons: AdminCustomerCoupon[];
  spinHistory: AdminCustomerSpinHistoryEntry[];
  rewardCoupons: AdminCustomerRewardCoupon[];
  notifications: AdminCustomerNotification[];
  paymentSummary: { paymentCount: number; totalCaptured: Money; totalRefunded: Money };
  refundSummary: { refundCount: number; totalRefunded: Money };
  loginActivity: AdminCustomerLoginActivity[];
  allowedActions: AccountAction[];
}

export interface CustomerListParams {
  search?: string;
  status?: UserStatus[];
  registeredFrom?: string;
  registeredTo?: string;
  sortField?: 'createdAt' | 'lastOrderAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface UpdateCustomerStatusInput {
  action: AccountAction;
  reason: string;
}

export interface UpdateCustomerStatusResult {
  status: string;
  alreadyInStatus: boolean;
}
